import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: string
  email: string
  displayName: string
  firstName: string
  lastName: string
  hospitalId?: string
  hospitalName?: string
  role: 'admin' | 'office' | 'doctor' | 'medical' | 'associate' | 'patient'
  doctorId?: string
  patientRecordId?: string // 患者の場合、紐づくカルテID
  avatarUrl?: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
}

// 医療従事者のサンプルユーザーデータ
const sampleUsers: Record<string, User & { password: string }> = {
  // 管理者・事務局
  'tanaka@fmt-japan.org': {
    id: 'user-002',
    email: 'tanaka@fmt-japan.org',
    password: 'password123',
    displayName: '田中三紀子',
    firstName: '三紀子',
    lastName: '田中',
    hospitalId: 'hosp-009',
    hospitalName: 'FMT事務局',
    role: 'admin',
  },
  'mizutani@fmt-japan.org': {
    id: 'user-001',
    email: 'mizutani@fmt-japan.org',
    password: 'password123',
    displayName: '事務局（水谷恭子）',
    firstName: '恭子',
    lastName: '水谷',
    hospitalId: 'hosp-009',
    hospitalName: 'FMT事務局',
    role: 'office',
  },
  // Baseクリニック赤坂の医師
  'yoshioka@base-clinic.jp': {
    id: 'user-003',
    email: 'yoshioka@base-clinic.jp',
    password: 'password123',
    displayName: '吉岡 紘平',
    firstName: '紘平',
    lastName: '吉岡',
    hospitalId: 'hosp-001',
    hospitalName: 'Baseクリニック赤坂',
    role: 'doctor',
    doctorId: 'doc-001',
    avatarUrl: '/avatars/yoshioka.jpg',
  },
  'sasaki@base-clinic.jp': {
    id: 'user-011',
    email: 'sasaki@base-clinic.jp',
    password: 'password123',
    displayName: '佐々木 美咲',
    firstName: '美咲',
    lastName: '佐々木',
    hospitalId: 'hosp-001',
    hospitalName: 'Baseクリニック赤坂',
    role: 'doctor',
    doctorId: 'doc-009',
  },
  // 城谷バイオウェルネスクリニックの医師
  'shirotani@shirotani-clinic.jp': {
    id: 'user-005',
    email: 'shirotani@shirotani-clinic.jp',
    password: 'password123',
    displayName: '城谷 昌彦',
    firstName: '昌彦',
    lastName: '城谷',
    hospitalId: 'hosp-003',
    hospitalName: '城谷バイオウェルネスクリニック',
    role: 'doctor',
    doctorId: 'doc-003',
  },
  'kimura@shirotani-clinic.jp': {
    id: 'user-012',
    email: 'kimura@shirotani-clinic.jp',
    password: 'password123',
    displayName: '木村 大輔',
    firstName: '大輔',
    lastName: '木村',
    hospitalId: 'hosp-003',
    hospitalName: '城谷バイオウェルネスクリニック',
    role: 'doctor',
    doctorId: 'doc-010',
  },
  // 二子玉川メディカルクリニックの医師
  'sato@futako-medical.jp': {
    id: 'user-006',
    email: 'sato@futako-medical.jp',
    password: 'password123',
    displayName: '佐藤 健一',
    firstName: '健一',
    lastName: '佐藤',
    hospitalId: 'hosp-004',
    hospitalName: '二子玉川メディカルクリニック',
    role: 'doctor',
    doctorId: 'doc-004',
  },
  // サンテクリニックの医師
  'yamada@sante-clinic.jp': {
    id: 'user-004',
    email: 'yamada@sante-clinic.jp',
    password: 'password123',
    displayName: '山田 太郎',
    firstName: '太郎',
    lastName: '山田',
    hospitalId: 'hosp-002',
    hospitalName: 'サンテクリニック',
    role: 'doctor',
    doctorId: 'doc-002',
  },
  // シンバイオシス研究所の医師
  'nakagawa@symbiosis.jp': {
    id: 'user-009',
    email: 'nakagawa@symbiosis.jp',
    password: 'password123',
    displayName: '中川 直哉',
    firstName: '直哉',
    lastName: '中川',
    hospitalId: 'hosp-008',
    hospitalName: 'シンバイオシス研究所',
    role: 'doctor',
    doctorId: 'doc-008',
  },
  // 患者ユーザー
  'patient-kukn@example.com': {
    id: 'patient-001',
    email: 'patient-kukn@example.com',
    password: 'patient123',
    displayName: 'KuKn',
    firstName: 'Kn',
    lastName: 'Ku',
    hospitalId: 'hosp-001',
    hospitalName: 'Baseクリニック赤坂',
    role: 'patient',
    patientRecordId: 'pat-001',
  },
  'patient-ituhrs@example.com': {
    id: 'patient-002',
    email: 'patient-ituhrs@example.com',
    password: 'patient123',
    displayName: 'ItuHrs',
    firstName: 'Hrs',
    lastName: 'Itu',
    hospitalId: 'hosp-004',
    hospitalName: '二子玉川メディカルクリニック',
    role: 'patient',
    patientRecordId: 'pat-002',
  },
  'patient-aihrchr@example.com': {
    id: 'patient-006',
    email: 'patient-aihrchr@example.com',
    password: 'patient123',
    displayName: 'AihrChr',
    firstName: 'Chr',
    lastName: 'Aihr',
    hospitalId: 'hosp-003',
    hospitalName: '城谷バイオウェルネスクリニック',
    role: 'patient',
    patientRecordId: 'pat-006',
  },
}

// 病院ごとの医師リスト
export const hospitalDoctors: Record<string, Array<{ id: string; name: string; specialty?: string }>> = {
  'hosp-001': [
    { id: 'doc-001', name: '吉岡 紘平', specialty: '消化器内科' },
    { id: 'doc-009', name: '佐々木 美咲', specialty: '精神科' },
  ],
  'hosp-002': [
    { id: 'doc-002', name: '山田 太郎', specialty: '内科' },
  ],
  'hosp-003': [
    { id: 'doc-003', name: '城谷 昌彦', specialty: '消化器内科' },
    { id: 'doc-010', name: '木村 大輔', specialty: '消化器内科' },
  ],
  'hosp-004': [
    { id: 'doc-004', name: '佐藤 健一', specialty: '内科' },
  ],
  'hosp-005': [
    { id: 'doc-005', name: '田村 一郎', specialty: '小児科' },
  ],
  'hosp-006': [
    { id: 'doc-006', name: '鈴木 花子', specialty: '小児科' },
  ],
  'hosp-007': [
    { id: 'doc-007', name: '高橋 誠', specialty: '小児科' },
  ],
  'hosp-008': [
    { id: 'doc-008', name: '中川 直哉', specialty: '研究' },
  ],
}

// 病院リスト
export const hospitals = [
  { id: 'hosp-001', name: 'Baseクリニック赤坂' },
  { id: 'hosp-002', name: 'サンテクリニック' },
  { id: 'hosp-003', name: '城谷バイオウェルネスクリニック' },
  { id: 'hosp-004', name: '二子玉川メディカルクリニック' },
  { id: 'hosp-005', name: '感謝クリニック' },
  { id: 'hosp-006', name: 'LAGOMクリニック' },
  { id: 'hosp-007', name: 'よろずクリニック' },
  { id: 'hosp-008', name: 'シンバイオシス研究所' },
]

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        // デモ用：サンプルユーザーでの認証
        const user = sampleUsers[email]
        
        if (!user || user.password !== password) {
          throw new Error('メールアドレスまたはパスワードが正しくありません')
        }

        // パスワードを除いたユーザー情報を設定
        const { password: _, ...userData } = user
        const token = `demo-token-${Date.now()}`

        set({
          user: userData,
          token,
          isAuthenticated: true,
        })
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },
    }),
    {
      name: 'fmt-japan-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
